import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InfoCallout } from '@/components/ui/info-callout';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useWizardFooterConfig,
  WIZARD_STEP_BODY_HEIGHT,
  WizardModalFooter,
  WizardStepBody,
  WizardStepper,
} from '@/components/wizards';
import {
  AI_BUDGET_CATEGORIES,
  AIBudgetCategory,
  AIBudgetStatus,
  useDeleteCategoryBudget,
  useSetAIBudget,
  useSetCategoryBudgets,
} from '@/hooks/useAIBudget';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import {
  adjustCategoryPercentage,
  buildInitialPercentages,
  buildPresetPercentages,
  CATEGORY_BUDGET_PRESETS,
  CategoryBudgetPresetKey,
  CategoryPercentages,
  percentagesToAllocations,
  validateCategoryPercentages,
} from '@/sections/settings/components/forms/categoryBudgetAllocation';
import {
  CATO_DEFAULT_CONFIG,
  CATO_SETTING_KEY,
  type CatoAutoTriageConfig,
} from '@/sections/settings/components/forms/catoAutoTriageConstants';
import { CatoAutoTriageStep } from '@/sections/settings/components/forms/CatoAutoTriageStep';
import {
  HUNT_SCHEDULE_DEFAULT_CONFIG,
  HUNT_SCHEDULE_SETTING_KEY,
  type HuntScheduleConfig,
} from '@/sections/settings/components/forms/huntScheduleConstants';
import { HuntScheduleStep } from '@/sections/settings/components/forms/HuntScheduleStep';
import { Setting } from '@/types';
import { cn } from '@/utils/classname';
import { formatUSD } from '@/utils/formatCurrency.util';

export interface AISpendBudgetWizardProps {
  email?: string;
  currentMaxLimit?: number;
  categories?: Partial<Record<AIBudgetCategory, AIBudgetStatus>>;
  canEditOverallBudget?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const allocationStyles: Record<AIBudgetCategory, string> = {
  hunts: 'bg-chariot-brand-primary',
  marcus: 'bg-chariot-brand-secondary',
  constantine: 'bg-chariot-brand-tertiary',
  cato: 'bg-chariot-status-medium',
  'h1-triage': 'bg-chariot-status-info',
};

const asInputValues = (percentages: CategoryPercentages) =>
  Object.fromEntries(
    Object.entries(percentages).map(([category, value]) => [
      category,
      value.toFixed(0),
    ])
  ) as Record<AIBudgetCategory, string>;

const asPercentages = (
  values: Record<AIBudgetCategory, string>
): CategoryPercentages =>
  Object.fromEntries(
    Object.entries(values).map(([category, value]) => [category, Number(value)])
  ) as CategoryPercentages;

export const AISpendBudgetWizard: React.FC<AISpendBudgetWizardProps> = ({
  email,
  currentMaxLimit,
  categories,
  canEditOverallBudget = true,
  open,
  onOpenChange,
}) => {
  const hasConfiguredCategories = AI_BUDGET_CATEGORIES.some(
    ({ key }) => categories?.[key]?.set
  );
  const [step, setStep] = useState(1);
  const [budgetValue, setBudgetValue] = useState(
    currentMaxLimit === undefined ? '' : String(currentMaxLimit)
  );
  const [enabled, setEnabled] = useState(hasConfiguredCategories);
  const [selectedPreset, setSelectedPreset] = useState<
    CategoryBudgetPresetKey | 'custom'
  >(hasConfiguredCategories ? 'custom' : 'balanced');
  const [values, setValues] = useState<Record<AIBudgetCategory, string>>(() =>
    asInputValues(buildInitialPercentages(currentMaxLimit ?? 0, categories))
  );
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cato auto-triage config (step 3)
  const { data: settings } = useMy({ resource: 'setting' }, { email });
  const existingCatoConfig = React.useMemo(() => {
    const setting = (settings ?? []).find(
      (s: Setting) => s.name === CATO_SETTING_KEY
    );
    if (!setting?.value || typeof setting.value !== 'object')
      return CATO_DEFAULT_CONFIG;
    return setting.value as CatoAutoTriageConfig;
  }, [settings]);
  const [catoConfig, setCatoConfig] =
    useState<CatoAutoTriageConfig>(CATO_DEFAULT_CONFIG);
  React.useEffect(() => {
    setCatoConfig(existingCatoConfig);
  }, [existingCatoConfig]);

  // Hunt schedule config
  const existingHuntConfig = React.useMemo(() => {
    const setting = (settings ?? []).find(
      (s: Setting) => s.name === HUNT_SCHEDULE_SETTING_KEY
    );
    if (!setting?.value || typeof setting.value !== 'object')
      return HUNT_SCHEDULE_DEFAULT_CONFIG;
    return setting.value as HuntScheduleConfig;
  }, [settings]);
  const [huntConfig, setHuntConfig] = useState<HuntScheduleConfig>(
    HUNT_SCHEDULE_DEFAULT_CONFIG
  );
  React.useEffect(() => {
    setHuntConfig(existingHuntConfig);
  }, [existingHuntConfig]);

  const huntsZeroed = enabled && Number(values.hunts ?? '0') <= 0;
  const catoZeroed = enabled && Number(values.cato ?? '0') <= 0;
  const totalSteps = 4;
  const steps = [
    { id: 'budget', title: 'Monthly budget' },
    { id: 'allocation', title: 'Category allocation' },
    { id: 'hunt-config', title: 'Hunt scheduling' },
    { id: 'cato-config', title: 'Cato auto-triage' },
  ];

  const { mutateAsync: setBudget } = useSetAIBudget({ email });
  const { mutateAsync: setCategoryBudgets } = useSetCategoryBudgets({ email });
  const { mutateAsync: deleteCategoryBudget } = useDeleteCategoryBudget({
    email,
  });
  const { mutateAsync: updateSetting } = useUpdateSetting(email);

  const parsedBudget = Number(budgetValue);
  const budgetError =
    !Number.isFinite(parsedBudget) || parsedBudget < 0
      ? 'Enter a non-negative dollar amount.'
      : null;
  const percentages = asPercentages(values);
  const allocations = percentagesToAllocations(parsedBudget, percentages);
  const allocationError = enabled
    ? parsedBudget > 0
      ? validateCategoryPercentages(parsedBudget, percentages)
      : 'Set a positive monthly budget to enable category allocation.'
    : null;
  const allocatedPercentage = Object.values(percentages).reduce(
    (total, value) => total + (Number.isFinite(value) ? value : 0),
    0
  );

  const setEditedPercentages = (next: CategoryPercentages) => {
    setValues(asInputValues(next));
    setSelectedPreset('custom');
    setError('');
  };

  const nudgePercentage = (category: AIBudgetCategory, direction: 1 | -1) => {
    setEditedPercentages(
      adjustCategoryPercentage(percentages, category, direction)
    );
  };

  const handlePercentageChange = (
    category: AIBudgetCategory,
    rawValue: string
  ) => {
    const current = percentages[category];
    const requested = Number(rawValue);
    if (
      rawValue !== '' &&
      Number.isInteger(current) &&
      Number.isInteger(requested) &&
      Math.abs(requested - current) === 1
    ) {
      const adjusted = adjustCategoryPercentage(
        percentages,
        category,
        requested > current ? 1 : -1
      );
      if (adjusted !== percentages) {
        setEditedPercentages(adjusted);
        return;
      }
    }
    setValues(previous => ({ ...previous, [category]: rawValue }));
    setSelectedPreset('custom');
    setError('');
  };

  const applyPreset = (presetKey: CategoryBudgetPresetKey) => {
    const preset = CATEGORY_BUDGET_PRESETS.find(({ key }) => key === presetKey);
    if (!preset) return;
    setValues(asInputValues(buildPresetPercentages(parsedBudget, preset)));
    setSelectedPreset(presetKey);
    setError('');
  };

  const deleteConfiguredCategories = async (onDeleted: () => void) => {
    const failed: string[] = [];
    let succeeded = 0;
    for (const { key, label } of AI_BUDGET_CATEGORIES) {
      if (!categories?.[key]?.set) continue;
      try {
        await deleteCategoryBudget({ category: key });
        succeeded++;
        onDeleted();
      } catch {
        failed.push(label);
      }
    }
    if (failed.length > 0) {
      throw new Error(
        `${succeeded > 0 ? `${succeeded} category allocation${succeeded === 1 ? '' : 's'} removed. ` : ''}Could not remove: ${failed.join(', ')}.`
      );
    }
  };

  const handleSave = async () => {
    if (budgetError || allocationError || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    const budgetChanged =
      canEditOverallBudget && parsedBudget !== (currentMaxLimit ?? 0);
    const loweringBudget =
      budgetChanged &&
      currentMaxLimit !== undefined &&
      parsedBudget < currentMaxLimit;
    let categoriesRemoved = false;
    let budgetUpdated = false;

    try {
      if (loweringBudget && hasConfiguredCategories) {
        await deleteConfiguredCategories(() => {
          categoriesRemoved = true;
        });
      }

      if (budgetChanged) {
        await setBudget({ maxLimit: parsedBudget });
        budgetUpdated = true;
      }

      if (enabled) {
        await setCategoryBudgets(allocations);
      } else if (hasConfiguredCategories && !categoriesRemoved) {
        await deleteConfiguredCategories(() => {
          categoriesRemoved = true;
        });
      }

      await updateSetting({
        name: HUNT_SCHEDULE_SETTING_KEY,
        value: huntConfig,
        silent: true,
      });

      await updateSetting({
        name: CATO_SETTING_KEY,
        value: catoConfig,
        silent: true,
      });

      toast.success('AI spend settings updated');
      onOpenChange(false);
    } catch (caught) {
      const detail = caught instanceof Error ? caught.message : 'Unknown error';
      const message = budgetUpdated
        ? `The monthly budget was updated, but the category allocation was not. ${detail}`
        : categoriesRemoved && budgetChanged
          ? `Category allocation changed, but the monthly budget was not updated. ${detail}`
          : categoriesRemoved
            ? `Category allocation was only partially removed. ${detail}`
            : `AI spend settings were not changed. ${detail}`;
      setError(message);
      toast.error('AI spend settings could not be updated', {
        description: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepId = steps[step - 1]?.id;
  const canGoNext =
    step === 1
      ? !budgetError && budgetValue !== ''
      : step === 2
        ? !allocationError
        : currentStepId === 'cato-config'
          ? catoConfig.sources.length > 0 && catoConfig.severities.length > 0
          : true;

  const footerConfig = useWizardFooterConfig({
    canGoNext,
    canGoBack: step > 1,
    isPending: isSubmitting,
    isLastStep: step === totalSteps,
    onNext: () => {
      setError('');
      setStep(s => s + 1);
    },
    onBack: () => {
      setError('');
      setStep(s => s - 1);
    },
    onCancel: () => onOpenChange(false),
    onSubmit: handleSave,
    labels: { submit: 'Save', submitting: 'Saving...' },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="ai-spend-budget-wizard">
        <DialogHeader>
          <DialogTitle>Edit AI Spend</DialogTitle>
          <DialogDescription>
            {currentStepId === 'budget'
              ? 'Set the overall monthly budget for AI usage.'
              : currentStepId === 'allocation'
                ? 'Optionally allocate the monthly budget across AI use cases.'
                : currentStepId === 'hunt-config'
                  ? 'Configure automatic hunt scheduling and target scope.'
                  : 'Configure which findings Cato automatically validates.'}
          </DialogDescription>
        </DialogHeader>

        <WizardStepper
          steps={steps}
          currentStep={step}
          variant="dots"
          ariaLabel="AI spend setup"
        />

        {currentStepId === 'budget' ? (
          <WizardStepBody align="top">
            <div className="flex flex-col gap-4" data-testid="budget-step">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="ai-spend-max-limit"
                  className="text-chariot-text-primary text-sm font-medium"
                >
                  Monthly spend limit (USD)
                </label>
                <p className="text-chariot-text-secondary text-xs">
                  AI calls stop after recorded monthly usage reaches this limit.
                  The final call may take usage above it. Resets on the first of
                  each month (UTC).
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-chariot-text-secondary text-sm">$</span>
                  <Input
                    id="ai-spend-max-limit"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={budgetValue}
                    onChange={event => {
                      setBudgetValue(event.target.value);
                      setError('');
                    }}
                    disabled={!canEditOverallBudget || isSubmitting}
                    aria-label="Monthly spend limit in dollars"
                    data-testid="ai-spend-max-limit-input"
                  />
                </div>
                {!canEditOverallBudget && (
                  <p className="text-chariot-text-secondary text-xs">
                    The monthly budget is managed by Praetorian. Continue to
                    manage its category allocation.
                  </p>
                )}
                {budgetError && (
                  <p className="text-chariot-stroke-critical text-xs">
                    {budgetError}
                  </p>
                )}
              </div>
            </div>
          </WizardStepBody>
        ) : currentStepId === 'allocation' ? (
          <WizardStepBody align="top">
            <div className="flex flex-col gap-5" data-testid="allocation-step">
              <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between gap-4 rounded-lg border px-4 py-3">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="category-budget-enabled"
                    className="text-chariot-text-primary text-sm font-medium"
                  >
                    Allocate by category
                  </label>
                  <span className="text-chariot-text-secondary text-xs">
                    Optional. Divide the {formatUSD(parsedBudget)} monthly
                    budget across AI use cases.
                  </span>
                </div>
                <Switch
                  id="category-budget-enabled"
                  checked={enabled}
                  onCheckedChange={checked => {
                    setEnabled(checked);
                    setError('');
                    if (checked && !hasConfiguredCategories)
                      applyPreset('balanced');
                  }}
                  disabled={isSubmitting || parsedBudget <= 0}
                  aria-label="Allocate budget by category"
                  data-testid="category-budget-enabled"
                />
              </div>

              {enabled ? (
                <>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {CATEGORY_BUDGET_PRESETS.map(preset => (
                      <Button
                        key={preset.key}
                        type="button"
                        variant="outline"
                        className={cn(
                          'h-auto min-h-16 flex-col items-start gap-1 px-3 py-2 text-left',
                          selectedPreset === preset.key &&
                            'border-chariot-brand-primary bg-chariot-brand-primary/10'
                        )}
                        aria-pressed={selectedPreset === preset.key}
                        onClick={() => applyPreset(preset.key)}
                        disabled={isSubmitting}
                        data-testid={`category-budget-preset-${preset.key}`}
                      >
                        <span>{preset.label}</span>
                        <span className="text-chariot-text-secondary text-xs font-normal">
                          {preset.description}
                        </span>
                      </Button>
                    ))}
                  </div>

                  <div
                    className="border-chariot-stroke flex h-7 w-full overflow-hidden rounded-md border"
                    aria-label="Category allocation preview"
                    data-testid="category-allocation-preview"
                  >
                    {AI_BUDGET_CATEGORIES.map(({ key, label }) => {
                      const percentage = Number.isFinite(percentages[key])
                        ? Math.max(0, percentages[key])
                        : 0;
                      return (
                        <div
                          key={key}
                          className={cn(
                            'border-chariot-background-primary flex min-w-0 items-center justify-center border-r last:border-r-0',
                            allocationStyles[key]
                          )}
                          style={{ width: `${percentage}%` }}
                          title={`${label}: ${percentage}% (${formatUSD(allocations[key])})`}
                          data-testid={`category-allocation-${key}`}
                        >
                          {percentage >= 15 && (
                            <span
                              className={cn(
                                'truncate px-1 text-xs font-semibold tabular-nums',
                                key === 'constantine'
                                  ? 'text-chariot-text-primary'
                                  : 'text-white'
                              )}
                            >
                              {percentage}%
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Use case</TableHead>
                        <TableHead>Used this month</TableHead>
                        <TableHead className="w-40">Allocation share</TableHead>
                        <TableHead className="text-right">
                          Monthly limit
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {AI_BUDGET_CATEGORIES.map(({ key, label }) => {
                        const usage = categories?.[key]?.current_usage ?? 0;
                        const belowUsage = allocations[key] < usage;
                        return (
                          <TableRow key={key}>
                            <TableCell className="font-medium">
                              {label}
                            </TableCell>
                            <TableCell className="tabular-nums">
                              <div className="flex flex-col gap-0.5">
                                <span>{formatUSD(usage)}</span>
                                {belowUsage && (
                                  <span className="text-chariot-stroke-high text-xs">
                                    Above new allocation
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  inputMode="numeric"
                                  min={0}
                                  max={100}
                                  step="1"
                                  value={values[key]}
                                  disabled={isSubmitting}
                                  aria-label={`${label} allocation percentage`}
                                  onKeyDown={event => {
                                    if (
                                      event.key !== 'ArrowUp' &&
                                      event.key !== 'ArrowDown'
                                    )
                                      return;
                                    event.preventDefault();
                                    nudgePercentage(
                                      key,
                                      event.key === 'ArrowUp' ? 1 : -1
                                    );
                                  }}
                                  onChange={event =>
                                    handlePercentageChange(
                                      key,
                                      event.target.value
                                    )
                                  }
                                  data-testid={`cat-budget-${key}-input`}
                                />
                                <span className="text-chariot-text-secondary">
                                  %
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatUSD(allocations[key])}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <div
                    className="flex items-center justify-between gap-4 text-xs tabular-nums"
                    role="status"
                    aria-live="polite"
                  >
                    {allocationError ? (
                      <span className="text-chariot-stroke-critical font-medium">
                        {allocationError}
                      </span>
                    ) : (
                      <span className="text-chariot-stroke-info flex items-center gap-1 font-medium">
                        <CheckCircleIcon className="size-4" /> 100% allocated
                      </span>
                    )}
                    <span className="text-chariot-text-secondary shrink-0">
                      {allocatedPercentage}% of 100%
                    </span>
                  </div>
                </>
              ) : (
                <InfoCallout>
                  Category allocation is optional. AI requests will share the
                  overall monthly budget.
                </InfoCallout>
              )}
            </div>
          </WizardStepBody>
        ) : currentStepId === 'hunt-config' ? (
          <div
            className={cn('flex flex-col', WIZARD_STEP_BODY_HEIGHT)}
            data-testid="hunt-config-step"
          >
            <HuntScheduleStep
              config={huntConfig}
              onChange={setHuntConfig}
              disabled={isSubmitting || huntsZeroed}
            />
          </div>
        ) : (
          <div
            className={cn('flex flex-col', WIZARD_STEP_BODY_HEIGHT)}
            data-testid="cato-config-step"
          >
            <CatoAutoTriageStep
              config={catoConfig}
              onChange={setCatoConfig}
              disabled={isSubmitting || catoZeroed}
            />
          </div>
        )}

        {error && (
          <InfoCallout
            variant="error"
            icon={<ExclamationTriangleIcon className="size-5" />}
            role="alert"
            data-testid="ai-spend-error"
          >
            {error}
          </InfoCallout>
        )}

        <WizardModalFooter config={footerConfig} />
      </DialogContent>
    </Dialog>
  );
};
